/**
 * The card beside the grid: the survey whose column the trainer pointed at.
 *
 * ── What the canon draws here that the contract cannot answer ────────────────────────────────
 * KANON `02` puts a „Ten cykl · pon 14 wrz" band on this card with three figures — wypełnione,
 * spóźniona, pominięta. Nothing on the wire carries them: `GET /survey/templates` has a title, a
 * cadence, a question count and a roster, and `/survey/review-queue` knows only about submissions
 * nobody has read. Counting a cycle would need a per-template occurrence read that does not exist.
 * The band is therefore NOT drawn, and the figure that stands in its place is one this screen
 * genuinely knows: how many of the clients in the grid hold this survey. Recorded as a contract gap
 * in `AGENTS.md`, not faked.
 *
 * ── It describes; it does not edit ───────────────────────────────────────────────────────────
 * Same rule as the Ankiety tab's card, and the same reason: the one way into a form is the pencil,
 * which opens `/raporty/$id` in block C. Until then it is drawn REFUSED — see `soonControl`.
 *
 * ── Its box never changes with its state, and that was MEASURED ──────────────────────────────
 * Every band is drawn in every state — the title row, the chip row, both rules, the assigned figure,
 * the „Pytania" head, five question rows and the button under them — with bars in place of the words
 * while the read is open. The first cut drew the skeleton ALONE and the card grew from 88px to
 * 345.25px when the answer landed, measured per animation frame with 900ms of latency injected. The
 * bands are the reserve; the gate only decides whether the bars inside them are painted.
 *
 * ── The card hands over as ONE box ───────────────────────────────────────────────────────────
 * Every band forks on `shown`, which is the row ONLY once the card is out of its gated wait. The
 * row alone would put the title, the chips and the foot on screen the moment the read answered —
 * up to 300ms before the gate retires the bars in the question list under them — so the card would
 * be half words and half bars for the length of the hand-over.
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';

import { EmptyState } from '~/shared/ui/EmptyState';
import { NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import iconButton from '~/shared/ui/iconButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { MatrixTally } from '../model/matrix';
import type { DetailSection } from '~/features/survey-question-editor';
import {
  cadenceLabel,
  questionCountLabel,
  statusLabel,
  type SurveyRow,
} from '../model/surveyList';
import type { MatrixCardState } from '../lib/useMatrixTab';
import { ClockIcon, PencilIcon } from '~/shared/ui/ReportIcons';
import styles from './Matrix.module.css';

/** Five, which is what the canon lists before it hands over to „Wszystkie N pytań ›". */
const PREVIEW_QUESTIONS = 5;

function QuestionSkeleton({ skeleton }: { readonly skeleton: boolean }) {
  const { t } = useTranslation();
  return (
    <ul className={styles.questions} aria-busy="true">
      <li className={hidden.srOnly}>{t('web:reports.matrix.card.loading')}</li>
      {Array.from({ length: PREVIEW_QUESTIONS }, (_, row) => (
        <li key={row} className={styles.question}>
          {skeleton ? <i className={`${String(skel.bar)} ${String(styles.skelQuestion)}`} /> : null}
        </li>
      ))}
    </ul>
  );
}

interface MatrixCardProps {
  readonly state: MatrixCardState;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly row: SurveyRow | null;
  readonly sections: readonly DetailSection[];
  /** How many of the clients in the grid hold this survey. See the header. */
  readonly tally: MatrixTally | null;
}

export function MatrixCard({ state, skeleton, reveal, row, sections, tally }: MatrixCardProps) {
  const { t } = useTranslation();

  // Nothing to describe, and the grid has answered: the card says so instead of standing empty. It
  // is the only state that replaces the bands, and it is reachable only when the grid is empty too,
  // so no geometry moves under it.
  if (row === null && state !== 'pending') {
    return (
      <aside className={styles.panel} aria-label={t('web:reports.matrix.card.noneTitle')}>
        <div className={styles.panelState}>
          <EmptyState
            mark={<NoteMarkIcon />}
            tone="neutral"
            title={t(
              state === 'error'
                ? 'web:reports.matrix.card.errorTitle'
                : 'web:reports.matrix.card.noneTitle',
            )}
            hint={t(
              state === 'error'
                ? 'web:reports.matrix.card.errorMessage'
                : 'web:reports.matrix.card.noneMessage',
            )}
          />
        </div>
      </aside>
    );
  }

  // The row once the wait is over, and `null` for the whole of it. See the header.
  const shown = state === 'pending' ? null : row;
  const cadence = shown === null ? null : cadenceLabel(shown, t);
  const status = shown === null ? null : statusLabel(shown, t);
  const preview = sections.flatMap((section) => section.questions).slice(0, PREVIEW_QUESTIONS);

  return (
    <aside className={styles.panel} aria-label={shown?.title ?? t('web:reports.matrix.card.noneTitle')}>
      <div className={styles.panelHead}>
        {shown === null ? (
          <i className={`${String(skel.bar)} ${String(styles.skelTitle)}`} />
        ) : (
          <h2 className={`${String(styles.panelTitle)} ${reveal}`}>{shown.title}</h2>
        )}
        {/* Drawn in every state so the head does not move when the read lands; refused while
          * there is no row to edit, because a link with no id has nowhere to go. */}
        {shown === null ? (
          <span className={`${String(iconButton.iconButton)} ${String(styles.panelEdit)}`} aria-hidden />
        ) : (
          <Link
            to="/raporty/$templateId"
            params={{ templateId: String(shown.id) }}
            className={`${String(iconButton.iconButton)} ${String(styles.panelEdit)}`}
            aria-label={t('web:reports.surveys.detail.edit')}
            title={t('web:reports.surveys.detail.edit')}
          >
            <PencilIcon className={styles.panelEditIcon} />
          </Link>
        )}
      </div>

      <p className={`${String(styles.meta)} ${shown === null ? '' : reveal}`}>
        {shown === null ? (
          <i className={`${String(skel.bar)} ${String(styles.skelChip)}`} />
        ) : (
          <>
            {cadence === null ? null : (
              <span className={styles.chip}>
                <ClockIcon className={styles.chipIcon} />
                {cadence}
              </span>
            )}
            <span>{questionCountLabel(shown, t)}</span>
            {status === null ? null : (
              <span className={shown.active ? styles.status : styles.statusPaused}>{status}</span>
            )}
          </>
        )}
      </p>

      <div className={styles.rule} />

      {/* The figure the contract does answer, in the band the canon spent on a cycle it does not. */}
      <p className={styles.assigned}>
        {tally === null
          ? null
          : t('web:reports.matrix.card.assigned', { held: tally.held, total: tally.total })}
      </p>

      <div className={styles.rule} />

      <span className={styles.sectionLabel}>{t('web:reports.matrix.card.questionsHead')}</span>

      {shown === null ? (
        <QuestionSkeleton skeleton={skeleton} />
      ) : state === 'error' ? (
        <ul className={styles.questions}>
          <li className={styles.question}>
            <span className={styles.questionBlank}>
              {t('web:reports.matrix.card.errorMessage')}
            </span>
          </li>
        </ul>
      ) : (
        <ul className={`${String(styles.questions)} ${reveal}`}>
          {preview.map((question) => (
            <li key={question.key} className={styles.question}>
              <span className={styles.questionLabel}>{question.label}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Drawn in every state, so the card's foot is where it will be from the first frame. It
        * leads to the EDITOR, which is where every question of a form is listed — this card shows
        * the first few and the editor shows them all. */}
      {shown === null ? (
        <span className={styles.allQuestions} aria-hidden>
          {t('web:reports.matrix.card.allQuestions', { count: 0 })}
        </span>
      ) : (
        <Link
          to="/raporty/$templateId"
          params={{ templateId: String(shown.id) }}
          className={styles.allQuestions}
        >
          {t('web:reports.matrix.card.allQuestions', { count: shown.questionCount })}
        </Link>
      )}
    </aside>
  );
}
